import { BookOpen, Shield, Users, Layers3, Clock, Lock, Compass } from "lucide-react";
import { Eyebrow, H2, P, Status, PageHero, Section } from "@/components/SharedUI";
import Term, { TERMS } from "@/components/Term";

const layers = [
  ["Story", "Origin", "Enter through Bigibila, old people, quills and responsibility."],
  ["Girribal", "Learning", "Teach the user through passage, reflection and knowledge gates."],
  ["Governance", "Structure", "Translate story into decision rights, authority maps and operating rules."],
  ["Protection", "ICIP / IDS", "Hold knowledge through permissions, access levels, consent and audit evidence."],
  ["Future", "Bigibila Systems", "Move from toolkit into protected software, licence pathways and sovereign infrastructure."],
];

const animals = [
  ["Bigibila", "Primary", "Protection, governance, memory, accountability and sovereignty.", "Use as the gateway, logo logic, onboarding, protected layers and governance shield."],
  ["Mindjarru / Weebills", "Secondary", "Movement, information, communication, hidden intent and shifting power.", "Use carefully for notifications, messaging, data movement and system events."],
  ["Dhinawan / Emu", "Optional", "Resource, abundance, community asset and shared responsibility.", "Use for knowledge repositories, archives and community hubs."],
];

const principles = [
  [BookOpen, "Story before system", "The user should feel they entered a living governance space, not just opened software. Use journey, progression, breathing space and layered reveals."],
  [Shield, "Quills are the protection system", "Quills become dividers, pathway markers, permission indicators, governance gates, hover states and protected-access cues."],
  [Users, "Animals carry teaching", "Animal symbols should act as governance archetypes and relational teaching carriers, never tourism branding or generic decoration."],
  [Layers3, "No generic dot backgrounds", "Use restrained symbolism, linework, pathways, topology, quill geometry and carved-space aesthetics."],
  [Clock, "Slow interface principle", "The experience should breathe through subtle fades, measured transitions and ceremonial pacing."],
  [Lock, "The site feels protected", "Use partial reveals, story gates, permission pathways, shadow/light transitions and subtle lock metaphors through quills."],
];

export default function DesignPrinciples() {
  return (
    <>
      <PageHero
        eyebrow="Core Bigibila design principles"
        title="A governance experience — ceremonial, intelligent, grounded, relational, protective, modern and ancient."
        desc={<>This visual system avoids generic Indigenous corporate design. It uses story, quills, layered knowledge, protected pathways and restrained symbolism to make users feel that knowledge is cared for. <Term def={TERMS.Girribal}>Girribal</Term> — the learning passage — runs through everything: the site teaches as you move.</>}
      />

      {/* GIRRIBAL — definition + Bigibila interpretation */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-3">
                Girribal <span className="text-stone-400 normal-case tracking-normal text-[11px]">(YR, YY, GR) · noun</span>
              </div>
              <H2 className="text-3xl md:text-5xl">
                A traditional riddle that teaches.
              </H2>
              <p className="mt-6 text-stone-800 leading-[1.85] text-lg max-w-2xl">
                A girribal is a traditional riddle, puzzle, or question-and-answer game that was widely enjoyed as a social and educational activity. Girribals were used to share knowledge, test observation skills, encourage critical thinking, and strengthen community connections through storytelling, performance and humour.
              </p>
              <p className="mt-5 text-stone-700 leading-[1.85] text-base max-w-2xl">
                Historical accounts describe girribals as more than simple riddles. They were often presented as songs, stories or dramatic performances where clues were woven into the telling. Participants would listen carefully, interpret the meanings, and identify the answer.
              </p>
              <p className="mt-5 text-stone-700 leading-[1.85] text-base max-w-2xl">
                Girribals played an important role in cultural learning, communication and entertainment. A person who knew many girribals was often respected for their knowledge, creativity, and ability to engage others.
              </p>
            </div>

            {/* Example girribal card */}
            <div className="lg:col-span-5">
              <div className="border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-8 lg:sticky lg:top-28">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-4">
                  Example girribal
                </div>
                <p className="font-display text-2xl md:text-3xl text-stone-900 leading-snug italic">
                  "What says to the floodwater, <span className="text-[#9C4221]">'I am stronger than you; you cannot push me back'</span>?"
                </p>
                <div className="mt-8 pt-6 border-t border-[#9C4221]/20">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-2">Answer</div>
                  <div className="font-display text-3xl tracking-tight font-bold text-stone-900">Guduu</div>
                  <div className="text-sm text-stone-600 mt-1">(codfish)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bigibila Systems interpretation */}
          <div className="border border-stone-200">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              <div className="lg:col-span-5 bg-[#FAFAF9] p-8 lg:border-r border-b lg:border-b-0 border-stone-200">
                <Eyebrow>Bigibila Systems™ interpretation</Eyebrow>
                <H2 className="text-2xl md:text-3xl">
                  A knowledge gate that requires learning before access.
                </H2>
                <p className="mt-5 text-stone-700 leading-relaxed">
                  Rather than using passwords, users engage with questions, stories and cultural knowledge. Each girribal teaches a lesson and demonstrates understanding before moving to the next stage.
                </p>
                <p className="mt-4 text-stone-700 leading-relaxed">
                  Access is earned through learning, reflection and knowledge-sharing — not by entering a password.
                </p>
              </div>

              <div className="lg:col-span-7 bg-stone-50 p-8">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-5">Transformation</div>
                <div className="grid gap-px bg-stone-200 border border-stone-200">
                  {[
                    ["Password", "Girribal"],
                    ["Authentication", "Learning"],
                    ["Access Control", "Knowledge Responsibility"],
                    ["User Journey", "Cultural Learning Journey"],
                  ].map(([from, to]) => (
                    <div key={from} className="bg-[#FAFAF9] grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 py-4">
                      <span className="text-stone-500 text-sm line-through decoration-stone-400">{from}</span>
                      <span className="text-stone-400 text-sm">→</span>
                      <span className="text-stone-900 font-display font-bold text-base">{to}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-stone-700 italic leading-relaxed">
                  This transforms a login process into a culturally grounded learning experience.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* Design spine */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>Design spine</Eyebrow>
              <H2 className="text-3xl md:text-4xl">The whole site moves through five layers.</H2>
              <P className="mt-6">
                The experience should feel like a pathway: from origin, through learning, into governance, into protection, and finally into the future Bigibila Systems product environment.
              </P>
            </div>
            <div className="lg:col-span-7 grid gap-px bg-stone-200 border border-stone-200">
              {layers.map(([layer, meaning, desc], i) => (
                <div key={layer} className="bg-[#FAFAF9] p-5 grid gap-3 md:grid-cols-[60px_140px_1fr] md:items-center">
                  <div className="font-display text-2xl font-bold text-[#9C4221]">0{i + 1}</div>
                  <div>
                    <div className="font-display font-bold text-stone-900">{layer}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">{meaning}</div>
                  </div>
                  <p className="text-sm leading-relaxed text-stone-600">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* Experience rules */}
      <section className="border-b border-stone-200">
        <Section>
          <Eyebrow>Experience rules</Eyebrow>
          <H2>Make it feel protected, not busy.</H2>
          <div className="mt-10 grid gap-px bg-stone-200 border border-stone-200 md:grid-cols-2 lg:grid-cols-3">
            {principles.map(([Icon, title, desc]) => (
              <div key={title} className="bg-[#FAFAF9] p-7 hover-rise">
                <Icon className="h-5 w-5 text-[#9C4221] mb-5" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-lg tracking-tight text-stone-900">{title}</h3>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Quill access language */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-8">
            <Eyebrow>Quill access language</Eyebrow>
            <H2 className="text-3xl md:text-4xl">Public → Protected → Restricted.</H2>
            <div className="mt-10 grid gap-px bg-stone-200 border border-stone-200 md:grid-cols-3">
              {[
                ["Public", "Small quill", "Open information, public-facing story and general product explanation."],
                ["Protected", "Larger quill", "Toolkit samples, governance methods, controlled demo and client-specific pathways."],
                ["Restricted", "Full quill shield", "Private provenance, sensitive cultural material, legal/IP material and client evidence packs."],
              ].map(([level, symbol, desc]) => (
                <div key={level} className="bg-[#FAFAF9] p-6">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9C4221]">{symbol}</div>
                  <h3 className="mt-2 font-display text-2xl tracking-tight font-bold text-stone-900">{level}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-700">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* Animal system */}
      <section className="border-b border-stone-200">
        <Section>
          <Eyebrow>Animal system</Eyebrow>
          <H2>Animals are governance archetypes, not decoration.</H2>
          <div className="mt-10 grid gap-px bg-stone-200 border border-stone-200 lg:grid-cols-3">
            {animals.map(([name, role, meaning, usage]) => (
              <div key={name} className="bg-[#FAFAF9] p-8 hover-rise">
                <div className="flex items-center justify-between mb-5">
                  <Compass className="h-6 w-6 text-[#9C4221]" strokeWidth={1.5} />
                  <Status>{role}</Status>
                </div>
                <h3 className="font-display text-2xl tracking-tight font-bold text-stone-900">{name}</h3>
                <p className="mt-3 text-sm text-stone-700 leading-relaxed">{meaning}</p>
                <div className="mt-5 border border-stone-200 bg-stone-50 p-4 text-xs leading-relaxed text-stone-600">{usage}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Colour and visual direction */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>Colour and visual direction</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Indigenous luxury governance system.</H2>
              <P className="mt-6">
                The direction should feel like Indigenous futurism, a luxury museum system, cinematic archive, ethical AI interface, protected knowledge vault and sovereign digital architecture.
              </P>
            </div>
            <div className="lg:col-span-7 grid gap-px bg-stone-200 border border-stone-200 md:grid-cols-3">
              {[
                ["Deep charcoal", "Country at night, seriousness, governance and authority.", "bg-stone-900"],
                ["Ochre gold", "Knowledge, ceremony, responsibility and continuity.", "bg-[#9C4221]"],
                ["Soft bone", "Memory, archive, story and respectful warmth.", "bg-[#E4B58E]"],
              ].map(([title, desc, color]) => (
                <div key={title} className="bg-[#FAFAF9] p-6">
                  <div className={`h-16 ${color} mb-4`} />
                  <h3 className="font-display font-bold text-stone-900">{title}</h3>
                  <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
